import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { desktop, mobile } from '../styles/media'
import translate from '../helper/translate'
import Icon from '../components/general/Icon'
import { discordLink } from '../../config'
import LogoImage from '../images/logo/cardcluster-white.svg'
import Link from '../components/router/Link'
import { getHelpLink } from '../helper/links'
import Content from '../components/layout/Content'
import BetterButton, { StyledBetterButton } from '../components/button/BetterButton'
import themeLight from '../styles/theme-light'
import AdSlot from './Ads/AdSlot'

const Footer = ({ authToken, mobileMenu, onBeforeNavigate, t, userLanguage }) => {
	const nav = [
		{
			name: 'general.support',
			to: '/support',
			icon: 'mail'
		},
		{
			name: 'general.help',
			to: getHelpLink(userLanguage),
			external: true
		},
		{
			cmp: <div data-fuse-privacy-tool></div>,
			to: '#'
		},
		{
			name: 'general.collectionManager',
			to: '/product/collection-manager'
		},
		{
			name: 'general.cardclusterPro',
			to: '/pricing'
		},
		{
			name: 'general.changelog',
			to: '/changelog'
		},
		{
			name: 'general.roadmap',
			to: '/roadmap'
		},
		{
			name: 'general.imprint',
			to: '/legal'
		},
		{
			name: 'general.privacy',
			to: '/privacy'
		},
		{
			name: 'general.terms',
			to: '/terms'
		},
		{
			name: 'copyright',
			to: '/'
		}
	]

	return (
		<>
			{!mobileMenu && <AdSlot name="incontent_footer" />}
			<StyledFooter>
				<Content>
					<Text>
						<Logo><img src={LogoImage} alt="cardcluster Logo" /></Logo>
					</Text>

					<Slogan>
						{t('general.sloganNew')}
					</Slogan>

					<AboutButtons>
						<BetterButton small color={themeLight.secondary} to="/about" onBeforeNavigate={onBeforeNavigate}>
							{t('general.about')}
						</BetterButton>
						<BetterButton small color={themeLight.secondary} to="/advertise" onBeforeNavigate={onBeforeNavigate}>
							{t('general.advertiseOnCardcluster')}
						</BetterButton>
					</AboutButtons>

					<Disclaimer>
						<p><b>{t('general.footerLinkDisclaimer')}</b></p>

						<p>{t('general.footerLinkDisclaimerText')}</p>
					</Disclaimer>

					<Menu>
						{nav.map((item, index) => (
							<Fragment key={item.to}>
								{item.cmp && !authToken && item.cmp}
								{!item.cmp && (
									<Link
										to={item.to}
										external={item.external}
										onBeforeNavigate={onBeforeNavigate}
									>
										{item.icon && <><Icon name={item.icon} />&nbsp;</>}
										{item.name !== 'copyright' ? t(item.name) : `© ${new Date().getFullYear()} cardcluster`}
									</Link>
								)}
								{(!item.cmp || !authToken) && index < nav.length - 1 && <>&nbsp;&middot;&nbsp;</>}
							</Fragment>
						))}
					</Menu>

					<Menu socials>
						<a href="/install">
							<Icon name="device-mobile" />
							{t('general.installApp')}
						</a>
						<MenuSeparator />
						<MenuInner>
							<a href="https://www.instagram.com/cardcluster" target="_blank" rel="noopener noreferrer">
								<Icon name="social-instagram" />
							</a>
							<a href="https://facebook.com/cardcluster" target="_blank" rel="noopener noreferrer">
								<Icon name="social-facebook" />
							</a>
							<a href="https://x.com/cardcluster" target="_blank" rel="noopener noreferrer">
								<Icon name="social-x" />
							</a>
							<a href="https://threads.com/cardcluster" target="_blank" rel="noopener noreferrer">
								<Icon name="social-threads" />
							</a>
							<a href="https://bsky.app/profile/cardcluster.bsky.social" target="_blank" rel="noopener noreferrer">
								<Icon name="social-bluesky" />
							</a>
							<a href="https://www.reddit.com/r/cardcluster" target="_blank" rel="noopener noreferrer">
								<Icon name="social-reddit" />
							</a>
							<a href={discordLink} target="_blank" rel="noopener noreferrer">
								<Icon name="social-discord" />
							</a>
							<a href="https://status.cardcluster.com" target="_blank" rel="noopener noreferrer">
								<svg className="svg-area" width="18px" height="18px" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
									<circle cx="9" cy="9" r="5" fill="#16a34a">
										<animate attributeName="r" values="5;9;9" dur="1s" repeatCount="indefinite"></animate>
										<animate attributeName="opacity" values="0.5;0" dur="1s" repeatCount="indefinite"></animate>
									</circle>
									<circle cx="9" cy="9" r="5" fill="#16a34a"></circle>
								</svg>
								{t('general.systemStatus')}
							</a>
						</MenuInner>
					</Menu>

					<AppVersion>{window.location.host} Version {process.env.VERSION}</AppVersion>
				</Content>
			</StyledFooter>
		</>
	)
}

Footer.propTypes = {
	authToken: PropTypes.string,
	mobileMenu: PropTypes.bool,
	onBeforeNavigate: PropTypes.func,
	t: PropTypes.func.isRequired,
	userLanguage: PropTypes.string.isRequired
}

Footer.defaultProps = {
	authToken: null,
	mobileMenu: false,
	onBeforeNavigate: undefined
}

const StyledFooter = styled.footer`
	background: ${props => props.theme.backgroundLight};

	${Content} {
		display: flex;
		flex-direction: column;
		gap: 2rem;

		padding: 2rem !important;
	}

	${desktop`
		margin-top: 5rem;
	`}

	${mobile`
		margin-top: 2rem;
	`}
`

const Logo = styled.div`
	background: ${props => props.theme.header};
	border-radius: 4px;
	padding: 0.5rem 0.75rem;
	width: fit-content;

	img {
		height: 24px;
	}

	${props => props.theme.id === 'dark' && `
		background: transparent;
		padding: 0;
	`}
`

const Slogan = styled.p`
	font-size: 1rem;
	font-weight: 500;
	margin: -0.5rem 0 0;
`

const AboutButtons = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 1rem;

	${StyledBetterButton} {
		border-radius: 6px;
		font-size: 0.9rem;
		width: fit-content;
	}
`

const Text = styled.div`
	align-items: center;
	display: flex;
	justify-content: space-between;
	gap: 1rem;
`

const MenuSeparator = styled.div`
	background: ${props => props.theme.backgroundConcrete};
	height: 30px;
	width: 2px;
	margin: 0 0.5rem;
`

const MenuInner = styled.div`
	display: flex;
	gap: 0.75rem;
`

const Menu = styled.div`
	align-items: center;
	display: flex;
	flex-wrap: wrap;
	gap: 0.15rem 0;

	color: ${props => props.theme.textVeryLight};
	margin: 0 auto;

    a {
        color: ${props => props.theme.textVeryLight};
		font-size: 0.8rem;
		font-weight: 500;
		text-decoration: none;

		align-items: center;
		display: flex;

		svg {
			height: 16px;
			width: 16px;
		}

		${desktop`
			&:hover {
                color: ${props => props.theme.text};
			}
		`}

		&:last-child {
			margin-bottom: 0;
		}
	}

	${props => props.socials && `
		gap: 0.75rem;

		&, a {
			color: ${ props.theme.textLight};
		}

		a {
			gap: 0.15rem;
		}

		svg, a > svg {
			height: 20px;
			width: 20px;
		}
	`}

	${mobile`
		${props => props.socials && `
			flex-direction: column;

			${MenuSeparator} {
				height: 2px;
				width: 30px;
				margin: 0.5rem 0;
			}

			${MenuInner} {
				flex-wrap: wrap;
			}
		`}
	`}

`

const Disclaimer = styled.small`
	display: block;
	line-height: 1;
	max-width: 800px;

	p {
		color: ${props => props.theme.textVeryLight};
		font-size: 0.8rem;
		margin-bottom: 0.5rem;
	}
`

const AppVersion = styled.div`
	color: ${props => props.theme.textVeryLight};
	font-size: 0.7rem;
	font-style: italic;
	padding: 1rem 1rem 0;
	text-align: center;
`

const enhance = compose(
	connect(state => ({
		authToken: state.persist.authToken,
		userLanguage: state.persist.language
	})),
	translate('general')
)

export default enhance(Footer)
